import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import { AssetLoadingGate } from "./components/AssetLoadingGate/index.js";
import { AuraTriggeredToast } from "./components/AuraTriggeredToast/index.js";
import { BailPaidToast } from "./components/BailPaidToast/index.js";
import { BuildMenu } from "./components/BuildMenu/index.js";
import { CardDetailModal } from "./components/CardDetailModal/index.js";
import { CardDrawnToast } from "./components/CardDrawnToast/index.js";
import { CardEffectAckModal } from "./components/CardEffectAckModal/index.js";
import { CharSelect } from "./components/CharSelect/index.js";
import { ChernobylImpactModal } from "./components/ChernobylImpactModal/index.js";
import { ClimateVoteModal } from "./components/ClimateVoteModal/index.js";
import { CutsceneTriggerWatcher } from "./components/CutscenePlayer/CutsceneTriggerWatcher.js";
import { CutscenePlayer } from "./components/CutscenePlayer/index.js";
import { EnergyDilemmaModal } from "./components/EnergyDilemmaModal/index.js";
import { FacilityDestroyedToast } from "./components/FacilityDestroyedToast/FacilityDestroyedToast.js";
import { FossilPenaltyModal } from "./components/FossilPenaltyModal/index.js";
import { GameOverModal } from "./components/GameOverModal/index.js";
import { GiveCardPickerModal } from "./components/GiveCardPickerModal/index.js";
import { GrandTransitionOverlay } from "./components/GrandTransitionOverlay/index.js";
import { HandFullModal } from "./components/HandFullModal/index.js";
import { HandPanel } from "./components/HandPanel/index.js";
// R10 #7: HandSlotDrawer replaced by TileTopBar (tile-phase-only top bar w/ built-in drawer).
import { HeaderSidebar } from "./components/HeaderSidebar/index.js";
import { HexBoard } from "./components/HexBoard/index.js";
import { ImmunityBlockBanner } from "./components/ImmunityBlockBanner/index.js";
import { InmateEscapeModal } from "./components/InmateEscapeModal/index.js";
import { InsufficientFundsToast } from "./components/InsufficientFundsToast/index.js";
import { MaeCameo } from "./components/MaeCameo/index.js";
import { MainMenu } from "./components/MainMenu/index.js";
import { MasterHUD } from "./components/MasterHUD/index.js";
import { NewsTickerStrip } from "./components/NewsTickerStrip/index.js";
import { OrChoiceModal } from "./components/OrChoiceModal/index.js";
import { PauseMenu } from "./components/PauseMenu/index.js";
import { PlayerHUD } from "./components/PlayerHUD/index.js";
import { PolicyAuctionModal } from "./components/PolicyAuctionModal/index.js";
import { PollutionTaxModal } from "./components/PollutionTaxModal/index.js";
import { PostCutsceneDialogue } from "./components/PostCutsceneDialogue/index.js";
import { PrimaryActionZone } from "./components/PrimaryActionZone/index.js";
import { PrisonGuardModal } from "./components/PrisonGuardModal/index.js";
import { SeatHandoff } from "./components/SeatHandoff/index.js";
import { SidescrollerScene, USE_SIDESCROLLER_SCENE } from "./components/SidescrollerScene/index.js";
import { StolenCardToast } from "./components/StolenCardToast/index.js";
import { TargetPicker } from "./components/TargetPicker/index.js";
import { ThresholdEventBanner } from "./components/ThresholdEventBanner/index.js";
import { TileEffectsPanel } from "./components/TileEffectsPanel/index.js";
import { TilePicker, resolveValidTiles } from "./components/TilePicker/index.js";
import { TileStage } from "./components/TileStage/index.js";
import { TileTopBar } from "./components/TileTopBar/index.js";
import { TollPaidModal } from "./components/TollPaidModal/index.js";
import { TradeWindowModal } from "./components/TradeWindowModal/index.js";
import { TradingPrompt } from "./components/TradingPrompt/index.js";
import { useGameStore } from "./store/game-store.js";

/**
 * App root — WEB.4.UI.7 hot-seat game shell.
 * R5 #2: HexBoard CSS-scaled. R5 #2b: HandPanel always mounted.
 */

// BOARD_W / BOARD_H — intrinsic size of HexBoard canvas (px).
const BOARD_W = 900;
const BOARD_H = 540;

/** HexBoardScaler — CSS-transform-scales 900×540 child to fill available space. */
function HexBoardScaler({ children }: { children: React.ReactNode }): React.ReactElement {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const applyScale = useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const scale = Math.min(outer.clientWidth / BOARD_W, outer.clientHeight / BOARD_H);
    inner.style.transform = `scale(${scale})`;
    inner.style.transformOrigin = "top left";
    inner.style.left = `${Math.max(0, (outer.clientWidth - BOARD_W * scale) / 2)}px`;
    inner.style.top = `${Math.max(0, (outer.clientHeight - BOARD_H * scale) / 2)}px`;
  }, []);

  useEffect(() => {
    applyScale();
    const outer = outerRef.current;
    if (!outer) return;
    const ro = new ResizeObserver(applyScale);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [applyScale]);

  return (
    <div
      ref={outerRef}
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
      }}
      data-testid="hex-board-scaler"
    >
      <div ref={innerRef} style={{ position: "absolute", width: BOARD_W, height: BOARD_H }}>
        {children}
      </div>
    </div>
  );
}

export function App(): React.ReactElement {
  // WEB.4.UI.7: pass picker props to HexBoard from store
  const tilePickerOpen = useGameStore((s) => s.tilePickerOpen);
  const tilePickerFilter = useGameStore((s) => s.tilePickerFilter);
  const tilePickerMode = useGameStore((s) => s.tilePickerMode);
  const selectedPickerTile = useGameStore((s) => s.selectedPickerTile);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const selectPickerTile = useGameStore((s) => s.selectPickerTile);
  // WEB.4.MM (P0.3) — gate the gameplay shell on gamePhase.
  const gamePhase = useGameStore((s) => s.gamePhase);
  const playing = gamePhase === "playing";
  // R10 #7: hide HandPanel right column during tile phase — TileTopBar owns
  // the hand surface inside SidescrollerScene. Panel re-appears at idle.
  const turnPhase = useGameStore((s) => s.turnPhase);
  // R13 Bug #4/#5: gate HandPanel hide on BOTH turnPhase===landed AND
  // boardAnimationActive===false. Without the animation guard, the panel
  // disappears the instant the player rolls dice while the token is still
  // walking (turnPhase flips to "landed" immediately on rollDice, but the
  // scene should not cut in until the board walk finishes).
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  // WEB.4.CS codex R1 HIGH: while a cutscene is playing, mark the gameplay
  // shell as `inert` so hidden Roll Dice / HexBoard tiles can't be tabbed-to
  // or activated underneath the overlay.
  const cutsceneActive = useGameStore((s) => s.cutsceneGroupId !== null);
  // R9 #2/#9 — HandPanel ALWAYS mounted; never conditionally removed by turnPhase.
  // HandSlotDrawer is a complementary overlay when turnPhase=landed.
  // (Removing the `showRightHandPanel` gate fixes the recurring disappear regression.)

  const highlightedTiles =
    tilePickerOpen && tilePickerFilter !== null
      ? resolveValidTiles(
          tilePickerFilter,
          currentSeat,
          builtFacilities as Record<number, string | undefined>,
          ownedBy,
        )
      : undefined;

  // R5 #2: full-screen root
  return (
    <div
      className="flex flex-col bg-dark-bark"
      style={{ width: "100vw", height: "100vh", overflow: "hidden" }}
    >
      {/* Gameplay shell — only mounted when gamePhase==="playing". Includes
          every gameplay modal that owns global keyboard listeners so they
          unmount their listeners while the splash is up.

          During an active cutscene, wrap in an `inert` div so hidden controls
          can't be tabbed/clicked (codex CS R1 HIGH). The wrapper unmounts
          along with the children when gamePhase exits "playing". */}
      {playing && (
        <div
          // The `inert` HTML attribute disables every focusable descendant
          // and ignores their pointer events. React supports it as a prop in
          // recent versions; cast through `any`-shaped helper to avoid the
          // type-name mismatch across React 18 / 19 d.ts. The dataset attr is
          // for test introspection.
          {...({ inert: cutsceneActive ? "" : undefined } as {
            inert?: string;
          })}
          data-cutscene-active={cutsceneActive ? "true" : "false"}
          data-testid="gameplay-shell-wrapper"
          aria-hidden={cutsceneActive}
          style={{ display: "contents" }}
        >
          {/* N-3 — NewsTickerStrip: top-of-screen headline strip on round-wrap.
              z:590 — below modals (200+), above board. Auto-dismisses 5s.
              R13 Bug #8: punish_fossils skipped here — FossilPenaltyModal handles it. */}
          <NewsTickerStrip />

          {/* R13 Bug #8 — FossilPenaltyModal: centered modal for punish_fossils.
              z:595 — above NewsTickerStrip (590). Requires player Acknowledge.
              VISIBLE WHEN: currentHeadline?.effect === "punish_fossils" */}
          <FossilPenaltyModal />

          {/* R8 #4 — HeaderSidebar replaces horizontal TopBar.
              Fixed left column 200px wide with round badge, era, player chip,
              pollution gauge, fullscreen. */}
          <HeaderSidebar />

          <PlayerHUD />

          {/* R7 #1 — AuraPanel DELETED (6th relock of NO GLOBAL AURA). */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              width: "100%",
              display: "flex",
            }}
          >
            {/* HexBoardScaler — left 200px reserved for HeaderSidebar,
                right 280px always reserved for HandPanel (R9: always mounted). */}
            <div
              style={{
                flex: 1,
                marginLeft: 200, // R8 #4: HeaderSidebar reservation.
                marginRight: 280, // R9 #2: always reserve space for HandPanel column.
                minWidth: 0,
                minHeight: 0,
                display: "flex",
              }}
            >
              <HexBoardScaler>
                <HexBoard
                  {...(tilePickerOpen && highlightedTiles !== undefined
                    ? {
                        highlightedTiles,
                        selectedTile: selectedPickerTile,
                        onTileSelect: selectPickerTile,
                        pickerMode: tilePickerMode,
                      }
                    : {})}
                />
                <TilePicker />
              </HexBoardScaler>
            </div>

            {/* R9 #2/#9 / R10 #7 — HandPanel: mounted in right column but HIDDEN
                during tile phase (turnPhase=landed AND boardAnimationActive=false).
                R13 Bug #4/#5 fix: require boardAnimationActive===false so the panel
                stays visible while the token is walking — it only hides once the
                board walk finishes and the scene actually cuts in.
                TileTopBar replaces it during tile phase.
                During idle: always visible as before (no unmount = no regression). */}
            <div
              style={{
                position: "fixed",
                right: 0,
                top: 160,
                bottom: 0,
                width: 280,
                zIndex: 101,
                display: "flex",
                flexShrink: 0,
                // R13 #4/#5: hide only when tile phase is FULLY active (walk done).
                visibility: turnPhase === "landed" && !boardAnimationActive ? "hidden" : "visible",
              }}
              data-testid="hand-panel-column"
            >
              <HandPanel />
            </div>
            {/* R10 #7: HandSlotDrawer removed — replaced by TileTopBar drawer. */}
          </div>

          {/* SidescrollerScene — Phase 1 side-scrolling tile scene.
              USE_SIDESCROLLER_SCENE flag controls swap. Set flag false in
              SidescrollerScene.tsx to revert to TileStage fullscreen view. */}
          {USE_SIDESCROLLER_SCENE ? <SidescrollerScene /> : <TileStage />}

          {/* R10 #7 — TileTopBar: slim bar + hand drawer during tile phase.
              z:102 — above SidescrollerScene (100), below all modals.
              VISIBLE WHEN: turnPhase === "landed" (self-gates internally). */}
          <TileTopBar />

          {/* MasterHUD — legacy in-tile HUD used by the fullscreen TileStage
              path. R11.1 fix #2: skip it under SidescrollerScene because
              TileTopBar already shows the same seat/resource/era info and
              the two overlap at the top of the screen. */}
          {!USE_SIDESCROLLER_SCENE && <MasterHUD />}

          {/* PrimaryActionZone — fixed center-bottom, z:50 */}
          <PrimaryActionZone />

          {/* BuildMenu — overlay, opens when landing on buildable tile */}
          <BuildMenu />

          {/* CardDetailModal — overlay rendered above everything */}
          <CardDetailModal />

          {/* CardEffectAckModal — R3 Bug 6: blocks turn until player acknowledges effect */}
          <CardEffectAckModal />

          {/* TargetPicker — overlay for OpponentChosen cards, opens after CDM closes */}
          <TargetPicker />

          {/* Tile-effects status panel — shows auras/PEQ/immunities affecting current tile.
              Self-gates to turnPhase==="landed"; auto-hides under modals. */}
          <TileEffectsPanel />

          {/* GameOverModal — fullscreen ending overlay (WEB.4.UI.10b) */}
          <GameOverModal />

          {/* N-2 — ClimateVoteModal: fires first time total pollution crosses 100.
              z:1400 — above gameplay modals (200), below SeatHandoff (1500).
              VISIBLE WHEN: pendingClimateVote !== null AND boardAnimationActive===false.
              TEST PATH: endTurn until total pollution > 100 (no facilities needed; seed pollution). */}
          <ClimateVoteModal />

          {/* T-2 — PolicyAuctionModal: replaces bureaucrat card draw every 3rd policy-tile
              landing per seat. z:1600 — above ClimateVote (1400), below SeatHandoff (1500)...
              Actually above SeatHandoff since auction is player-input; SeatHandoff should not
              intercept during an auction. z=1600 to avoid overlap.
              VISIBLE WHEN: pendingAuction !== null AND boardAnimationActive === false.
              TEST PATH: land on Policy tile 3× with same seat → auction triggers on 3rd. */}
          <PolicyAuctionModal />

          {/* N-1 — EnergyDilemmaModal (R16 Builder-BX): 3-option energy trade-off minigame.
              Fires on terrain-tile landing when era >= 2. Per-landing one-shot.
              z:1300 — above HUD (≤610) and Mae cameo (400), below ClimateVote (1400).
              VISIBLE WHEN: pendingEnergyDilemma !== null AND boardAnimationActive === false.
              TEST PATH: era >= 2 → roll dice → land on terrain tile (Plains/Forest/Coast/etc.)
                         → EnergyDilemmaModal renders after HexBoard walk animation. */}
          <EnergyDilemmaModal />

          {/* Bug #12 — OrChoiceModal: player branch decision for OR-choice Event cards.
              Fires when isImmediate Event card has pay/risk branchId-annotated effects.
              Cards: Wildfire_Near_Facilities, Labor_Union_Strikes, Nuclear_Safety_Regulation,
              Flash_Flood. z:1350 — above EnergyDilemmaModal (1300), below ClimateVote (1400).
              VISIBLE WHEN: pendingOrChoice !== null AND boardAnimationActive === false.
              TEST PATH: roll dice → land on Event tile → draw OR-choice card
                         → OrChoiceModal renders after board walk animation. */}
          <OrChoiceModal />

          {/* T-3 — TradeWindowModal (R16 Builder-BY): 2-for-2 card swap minigame.
              Triggered by "Offer Card Swap" button on TradingPrompt.
              One-shot per game per drawer (tradeOfferedThisGame[seat]).
              z:1300 — above HUD (≤610) and Mae cameo (400), below ClimateVote (1400).
              VISIBLE WHEN: pendingTrade !== null AND boardAnimationActive === false.
              TEST PATH: land on Trading tile → click trader → "Offer Card Swap"
                         → TradeWindowModal renders (select phase). */}
          <TradeWindowModal />

          {/* R19-GC — GiveCardPickerModal: give-arm of Policy_Exchange SWAP.
              Fires after StealCard arm completes — drawer picks one own card to give.
              z:1350 — above OrChoiceModal (1350-range), below ClimateVote (1400).
              VISIBLE WHEN: pendingGiveCard !== null AND boardAnimationActive === false.
              TEST PATH: era 3 → play Policy_Exchange → pick opponent → StealCard fires
                         → GiveCardPickerModal renders for give-arm selection. */}
          <GiveCardPickerModal />

          {/* R20-GD-9 — ChernobylImpactModal: per-seat nuclear decommission summary.
              Fires after Chernobyl_Incident plays and at least one Nuclear plant is
              decommissioned (50% chance per plant via RngBranch resolver).
              z:1400 — top-level; same tier as ClimateVoteModal.
              VISIBLE WHEN: pendingChernobylImpact !== null.
              TEST PATH: play Chernobyl_Incident when any seat has a Nuclear facility
                         → modal shows plants lost + token refunds per seat. */}
          <ChernobylImpactModal />

          {/* T-4 — PollutionTaxModal (R16 Builder-BZ): fires on Policy-tile landing when
              era >= 3 and 10% RNG roll passes. Precedes auction in bureaucrat handler.
              z:1300 — above HUD (≤610) and Mae cameo (400), below ClimateVote (1400).
              VISIBLE WHEN: pendingPollutionTax !== null AND boardAnimationActive === false.
              TEST PATH: era >= 3 → land on Policy tile → repeat until 10% roll passes
                         → PollutionTaxModal renders for the drawer. */}
          <PollutionTaxModal />

          {/* StolenCardToast — transient notification on StealCard play (WEB.4.UI.12) */}
          <StolenCardToast />

          {/* DD Bug#10b — 1.5s toast when any facility is destroyed/decommissioned */}
          <FacilityDestroyedToast />

          {/* WEB.4.R2.CF — free card auto-draw toast ("Drew: X") */}
          <CardDrawnToast />

          {/* R3 Bug 1 — bail-paid leaving-jail toast */}
          <BailPaidToast />

          {/* R7 #8 — insufficient-funds takeover toast */}
          <InsufficientFundsToast />

          {/* R9 #10 — aura/passive triggered toast (toll halved, build free, etc.) */}
          <AuraTriggeredToast />

          {/* R4 — dramatic jail interaction modals (guard: pay bail, inmate: escape) */}
          <PrisonGuardModal />
          <InmateEscapeModal />

          {/* WEB.4.R2.CF — hand-full decision modal (replace slot or discard new) */}
          <HandFullModal />

          {/* TradingPrompt — Trading tile landing prompt (WEB.4.UI.13) */}
          <TradingPrompt />

          {/* ThresholdEventBanner — pollution-threshold crisis on era change (WEB.4.UI.14) */}
          <ThresholdEventBanner />

          {/* ImmunityBlockBanner — defender's shield intercepted an attack (WEB.4.UI.17) */}
          <ImmunityBlockBanner />

          {/* R15 CS.4 — MaeCameo: 2.5s Mae sprite cameo in bottom-right corner.
              z:400 — above tile UI (100-200), below modals (1000+).
              Fires at: era transition (joyful) / first quiz (idle) / first climate vote (sad).
              VISIBLE WHEN: pendingMaeCameo !== null AND boardAnimationActive === false.
              TEST PATH: play through an era boundary, or land on Event tile. */}
          <MaeCameo />

          {/* TollPaidModal — R5 #11 */}
          <TollPaidModal />

          {/* R6 #6 — Minimap removed per user request. HexBoard hover tooltip
              already shows tile details; minimap was redundant + clutter. */}

          {/* PauseMenu — Esc-triggered z:1200 overlay. Sits above gameplay
              modals (z:200) but below SeatHandoff (z:1500) so handoff still
              pre-empts pause. Esc handler is centralized inside PauseMenu. */}
          <PauseMenu />

          {/* SeatHandoff — full-screen z:1500 overlay between hot-seat turns.
              Mounted LAST so it sits above every other modal — its job is to
              hide the new player's private state until they take the device. */}
          <SeatHandoff />
        </div>
      )}

      {/* MainMenu — cold-start splash z:2000. Always mounted (handles its
          own gamePhase visibility gate) — when the user clicks Start, this
          becomes hidden and the CharSelect stub appears. */}
      <MainMenu />

      {/* R9 CharSelect — archetype selection stub. Shown between MainMenu and
          first game round (gamePhase="char-select"). Gates on its own phase. */}
      <CharSelect />

      {/* AssetLoadingGate — z:2300 (above CutscenePlayer z:2200). Shown once
          on char-select → playing transition. Fetches critical assets (intro
          BGM, player sprites, terrain BGs, IslandMap, facility sprites,
          era-1-a BGM — 50 URLs). Dismisses at 100% progress only. Stall
          detection: after STALL_MS (8s) of no progress, shows "Start anyway".
          Fast-skip: if assets cached and done within 300ms, never shown. */}
      <AssetLoadingGate />

      {/* CutscenePlayer — z:2200. Sits above MainMenu so intro plays over
          the menu fade. Always-mounted; gates on cutsceneGroupId. */}
      <CutscenePlayer />

      {/* R8 #6 — Post-cutscene dialogue chain (z:2100). Fires after every
          era-transition cutscene completes. Sequentially cycles all 4
          archetype speech bubbles (~3s each). */}
      <PostCutsceneDialogue />

      {/* R8 #6 — Grand transition bloom overlay (z:2050). Fires after the
          dialogue chain ends; 1.5s era-themed bloom into the new board era. */}
      <GrandTransitionOverlay />

      {/* CutsceneTriggerWatcher — headless. Watches era / gameStatus
          transitions and fires the matching cutscene. */}
      <CutsceneTriggerWatcher />
    </div>
  );
}
